import { useState, forwardRef, useImperativeHandle } from "react";
import { useApiGet } from "./api";
import classNames from "classnames";
import { capitalize, get, isObject, startCase } from "lodash";
import { Dropdown } from "react-bootstrap";
import { sortBy } from "lodash";

interface DataTableProps<T> {
  url: string;
  className?: string;
  params?: Record<string, any>;
  columns: {
    key: string;
    header?: string;
    headerClass?: string;
    cellClass?: string;
    align?: "left" | "center" | "right";
    value?: (value: any) => React.ReactNode | string;
    formatter?: (value: any) => React.ReactNode | string;
  }[];
  hideHeaders?: boolean;
  defaultSort?: [string, "asc" | "desc"];
  defaultFilters?: Record<string, any>;
  empty?: React.ReactNode | string;
}

interface DataTableHandle {
  refetch: () => void;
}

export const DataTable = forwardRef<DataTableHandle, DataTableProps<any>>(
  (props, ref) => {
    const { url, columns, params, hideHeaders, defaultSort, defaultFilters } = props;
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
      ...(defaultSort ? { sort: defaultSort[0], sortDir: defaultSort[1] } : {}),
      ...(defaultFilters ? { ...defaultFilters } : {}),
    });
    const { data, loaded, refetch } = useApiGet(url, { ...params, page, ...filters });

    useImperativeHandle(ref, () => ({ refetch: () => refetch() }));

    if (!loaded) {
      return null;
    }

    if (data.rows.length === 0 && props.empty) {
      return <div className="text-center">{props.empty || "No data"}</div>;
    }

    return (
      <div>
        <FiltersBar
          schema={data.filters}
          values={filters}
          setFilters={setFilters}
          setPage={setPage}
        />

        <table className={props.className || "table table-striped"}>
          <thead>
            <tr>
              {!hideHeaders &&
                columns.map((column) => (
                  <th
                    key={String(column.key)}
                    className={classNames(
                      column.headerClass,
                      column.align
                        ? `text-${
                            { left: "start", center: "center", right: "end" }[
                              column.align
                            ]
                          }`
                        : null
                    )}
                  >
                    {data.sortables.includes(column.key) ? (
                      <Sorter name={column.key} filters={filters} setFilter={setFilters}>
                        {column.header ?? startCase(column.key)}
                      </Sorter>
                    ) : (
                      column.header ?? startCase(column.key)
                    )}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i}>
                {columns.map((column) => (
                  <td
                    key={String(column.key)}
                    className={classNames(
                      column.cellClass,
                      column.align
                        ? `text-${
                            { left: "start", center: "center", right: "end" }[
                              column.align
                            ]
                          }`
                        : null
                    )}
                  >
                    {column.formatter
                      ? column.formatter(get(row, column.key))
                      : column.value
                      ? column.value(row)
                      : get(row, column.key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar metadata={data.metadata} setPage={setPage} />
      </div>
    );
  }
);

export const formatOptions = (
  options: (
    | string
    | { value: string; label?: string | JSX.Element; exclusive?: boolean }
  )[]
) => {
  if (isObject(options) && !Array.isArray(options)) {
    return Object.entries(options).map(([value, label]) => ({
      value: String(value),
      label,
    }));
  }

  return options.map((option) => {
    if (Array.isArray(option)) {
      return { value: String(option[0]), label: option[1] };
    }
    if (typeof option === "object") {
      return { label: option.value, ...option };
    }
    return { value: option, label: startCase(option) };
  });
};

const FilterMultiSelect = ({ name, placeholder, options, onChange, selected }) => {
  const [search, setSearch] = useState<string>(null);

  let filteredOptions = formatOptions(options).filter((o) =>
    search
      ? o.value.match(new RegExp(search, "i")) || o.label.match(new RegExp(search, "i"))
      : o.value
  );

  if (options.length > 10) {
    filteredOptions = sortBy(filteredOptions, (o) => !selected.includes(o.value));
  }

  const selectedLabels = formatOptions(options)
    .filter((o) => selected.includes(o.value))
    .map((o) => o.label);

  return (
    <Dropdown onToggle={(e) => setSearch("")} className="ml-2">
      <Dropdown.Toggle
        variant="outline-secondary btn-sm"
        id="dropdown-basic"
        className="d-flex align-items-center"
      >
        <div className="d-flex px-1 align-items-center">
          <div className="d-flex fs-14">
            {startCase(placeholder) || startCase(name)}:
            <div
              className="d-block bold ml-1"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selected.length === 0 && "All"}
              {selected.length === 1 && selectedLabels.join(", ")}
              {selected.length > 1 && `${selected.length} Selected`}
            </div>
          </div>
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className="w-200px">
        <div className="border-bottom ph-2 pb-1">
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <i className="bi bi-search" />
            </span>
            <input
              type="text"
              className="form-control"
              value={search || ""}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div
            className="fs-12 mt-1 pointer blue"
            onClick={() => {
              onChange([]);
            }}
          >
            Clear Selected
          </div>
        </div>
        <div className="filter-multi-select-options pt-1">
          {filteredOptions.map((o) => (
            <label className="input-group filter-multi-select-option" key={o.value}>
              <input
                type="checkbox"
                value={o.value}
                className="mr-1"
                checked={selected.includes(o.value)}
                onChange={(e) => {
                  const newValues = e.target.checked
                    ? [...selected, e.target.value]
                    : selected.filter((s) => s !== o.value);
                  onChange(newValues);
                }}
              />
              {o.label}
            </label>
          ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

const FiltersBar = ({ schema, values, setFilters, setPage }) => {
  return (
    <div className="d-flex align-items-center mb-3">
      {schema.map((filter) => (
        <>
          {filter.search ? (
            <div>
              <div
                className="input-group input-group-sm w-300px"
                style={{ flex: "1 0 300px" }}
              >
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) => {
                    setFilters({ ...values, [filter.field]: e.target.value });
                    setPage(0);
                  }}
                  value={values[filter.field] || ""}
                />
              </div>
            </div>
          ) : (
            (() => {
              let options = filter.values;
              if (filter.type === "presence") {
                options = ["Yes", "No"];
              }

              return (
                <FilterMultiSelect
                  key={filter.field}
                  name={filter.field}
                  placeholder={capitalize(filter.label || filter.field)}
                  options={options}
                  onChange={(selected) => {
                    setFilters({ ...values, [filter.field]: selected });
                    setPage(0);
                  }}
                  selected={values[filter.field] || []}
                />
              );
            })()
          )}
        </>
      ))}
      {Object.keys(schema || {}).length > 0 && (
        <button className="btn btn-link p-0 ml-3" onClick={() => setFilters({})}>
          <i className="bi bi-x gray"></i>
        </button>
      )}
    </div>
  );
};

const PaginationBar = ({ metadata, setPage }) => {
  if (metadata.total_count === 0) {
    return null;
  }

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <div className="small text-muted">
        {(metadata.current_page - 1) * metadata.per_page + 1}-
        {Math.min(metadata.current_page * metadata.per_page, metadata.total_count)} of{" "}
        {metadata.total_count}
      </div>

      {metadata.total_pages > 1 && (
        <div className="d-flex gap-2 align-items-center">
          <button
            onClick={() => setPage(1)}
            disabled={metadata.current_page === 1}
            className="btn btn-link p-0"
          >
            <i className="bi bi-chevron-bar-left"></i>
          </button>

          <button
            onClick={() => setPage(metadata.current_page - 1)}
            disabled={metadata.current_page === 1}
            className="btn btn-link p-0"
          >
            <i className="bi bi-chevron-left"></i>
          </button>

          <span>{metadata.current_page}</span>

          <button
            onClick={() => setPage(metadata.current_page + 1)}
            disabled={metadata.current_page === metadata.total_pages}
            className="btn btn-link p-0"
          >
            <i className="bi bi-chevron-right"></i>
          </button>

          <button
            onClick={() => setPage(metadata.total_pages)}
            disabled={metadata.current_page === metadata.total_pages}
            className="btn btn-link p-0"
          >
            <i className="bi bi-chevron-bar-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

const Sorter = ({ name, filters, setFilter, children }) => {
  const { sort, sortDir } = filters;
  const isCurrentSortColumn = sort === name;

  const onClick = () => {
    if (isCurrentSortColumn) {
      setFilter({ sort: name, sortDir: sortDir === "asc" ? "desc" : "asc" });
    } else {
      setFilter({ sort: name, sortDir: "asc" });
    }
  };
  const iconDir = { asc: "up", desc: "down" }[sortDir];

  return (
    <span className="pointer white-space-pre" onClick={onClick}>
      {children}
      {isCurrentSortColumn && (
        <i className={`bi bi-caret-${iconDir}-fill ml-1 fs-12 sort-caret`} />
      )}
    </span>
  );
};
